import React, { useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  Cards,
  ColumnLayout,
  Container,
  FormField,
  Icon,
  Input,
  SpaceBetween,
  Textarea,
  Header,
  Alert,
  Grid,
  Modal,
  Spinner,
  Box,
  Link,
  Tabs,
  ExpandableSection,
  Table,
  Select,
  Pagination,
} from "@amzn/awsui-components-react";

// Shared utilities from TemplateOak pattern
import { addAIApp, updateAIApp, getAIApp, deleteAppWithApproval, createAuditLog, getApprovalsForApp, getSuggestedLogins } from "./Utils/AppActivityUtilsAppsyncPlus";
import { getLogsById } from "../AuditLogging/AuditLogsAPIs";
import { getUserRatings, submitRating, deleteRating } from "src/utilities/RatingAPIs";
import StarRating from "src/Home/StarRating";
import { canAddEditDeleteApplication } from "./Utils/Permissions";
import { getEditableFields } from "./Utils/FormMetadata";
import { favorite, checkIsFavorite } from "../../utilities/Favorites";
import { featured, checkIsFeatured } from "../../utilities/Featured";
import { startWASApproval } from "../../utilities/ApprovalAPIs";
import { updateAutomatedApp } from "../../utilities/AutomatedAppsAPIs";
import { TEMPLATES, TEMPLATE_APP, AI_APP_SPECIFIC_FIELDS, TEMPLATE_APP_SOURCE_DATA_TABLES } from "./Constants";

// Custom input components
import CustomSelect from "./components/CustomSelect";
import CustomMultiSelect from "./components/CustomMultiSelect";
import CustomInput from "./components/CustomInput";
import CustomSelectAlias from "./components/CustomSelectAlias";
import CustomMultiHybridInputsForSourceTables from "./components/CustomMultiHybridInputsForSourceTables";
import { useSuggestedLogins, useDirectReports } from "src/hooks/useSuggestedLogins";
import ReactMarkdown from "react-markdown";
import SkeletonCard from "../../Home/SkeletonCard";
import AddToCPModal from "src/Home/ClosePortal/components/AddToCPModal";

function AIAppPage(props) {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);
  const userId = useSelector((globalState) => globalState.auth.userId);

  // Core state - renderJson/renderJsonEdited dual-state pattern (from TemplateOak)
  const [renderJson, setRenderJson] = useState(props?.renderJson || null);
  const [renderJsonEdited, setRenderJsonEdited] = useState(props?.renderJson || null);
  const [isEditMode, setIsEditMode] = useState(props?.isEditOverride || false);
  const [isNewApp, setIsNewApp] = useState(false);
  const [id, setId] = useState(props?.id || routeId || null);

  // Permission state
  const [canDelete, setCanDelete] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canAdd, setCanAdd] = useState(false);

  // Field metadata
  const [fieldMetadata, setFieldMetadata] = useState({});

  // UI state
  const [showLoadingIcon, setShowLoadingIcon] = useState(!props?.renderJson);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [alertErrorMessage, setAlertErrorMessage] = useState(null);
  const [updateComments, setUpdateComments] = useState("");
  const [modalError, setModalError] = useState("");

  // Favorites/Featured state
  const [isFavorite, setIsFavorite] = useState(false);
  const [favDisabled, setFavDisabled] = useState(false);
  const [featuredPressed, setFeaturedPressed] = useState(false);

  // Delete confirmation state
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [deleteAppName, setDeleteAppName] = useState("");

  // Audit/Change log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [changeLogPage, setChangeLogPage] = useState(1);
  const CHANGE_LOG_PAGE_SIZE = 5;

  // Close Portal modal state
  const [addToCPModalVisible, setAddToCPModalVisible] = useState(false);

  // User rating state
  const [userRating, setUserRating] = useState(0);
  const [displayAvgRating, setDisplayAvgRating] = useState(0);
  const [displayRatingCount, setDisplayRatingCount] = useState(0);

  // Alias lookup state (for CustomSelectAlias)
  const [suggestedLogins, setSuggestedLogins] = useState([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);

  // VP direct reports (same pattern as CreateTeamModal)
  const { directReports, directReportsLoading, getDirectReportsCaller } = useDirectReports();

  // --- Helper Functions (TemplateOak pattern) ---

  const getValue = (fieldName, subKey = null) => {
    if (subKey) {
      if (renderJsonEdited?.hasOwnProperty(fieldName)) {
        return renderJsonEdited[fieldName][subKey];
      } else {
        return null;
      }
    } else {
      return renderJsonEdited ? renderJsonEdited[fieldName] : null;
    }
  };

  const updateValue = (fieldName, value, subKey = null) => {
    let clone = JSON.parse(JSON.stringify(renderJsonEdited || {}));
    if (subKey) {
      if (!clone.hasOwnProperty(fieldName)) {
        clone[fieldName] = {};
      }
      clone[fieldName][subKey] = value;
    } else {
      clone[fieldName] = value;
    }
    setRenderJsonEdited(clone);
  };

  const isEditable = (fieldName, subKey = null) => {
    if (subKey) {
      return isNewApp || (isEditMode && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName][subKey] && fieldMetadata[fieldName][subKey]['editable']);
    } else {
      return isNewApp || (isEditMode && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName]['editable']);
    }
  };

  const getSelectFieldValues = (fieldName, subKey = null) => {
    if (subKey && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName][subKey] && fieldMetadata[fieldName][subKey]['values']) {
      return fieldMetadata[fieldName][subKey]['values'];
    } else if (fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName]['values']) {
      return fieldMetadata[fieldName]['values'];
    } else {
      return [];
    }
  };

  const getMultiSelectedOptions = (fieldName) => {
    let arr = renderJsonEdited ? renderJsonEdited[fieldName] : null;
    let selectedOptions = [];
    if (arr) {
      arr.forEach((item) => {
        selectedOptions.push({ label: item, value: item });
      });
    }
    return selectedOptions;
  };

  const getSuggestedLoginsCaller = async (fragment, size = 10) => {
    setSuggestedLogins([]);
    setAliasesLoading(true);
    const response = await getSuggestedLogins(fragment, size);
    const data = JSON.parse(response.data.getSuggestedLogins);
    let suggestions = data["suggestions"];
    suggestions = suggestions.map((login) => ({
      label: login?.toLowerCase(),
      value: login?.toLowerCase(),
    }));
    setSuggestedLogins(suggestions);
    setAliasesLoading(false);
    return suggestions;
  };

  // --- Display Helpers for View Mode ---

  const requiredLabel = (text) => <>{text} <span style={{color: "red"}}>*</span></>;

  const displayValue = (fieldName) => {
    const val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      return val.length > 0 ? val.join(", ") : "—";
    }
    return val;
  };

  const displayLink = (fieldName) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
      if (val == null || val === "") return "—";
    }
    return <Link href={val} external>{val}</Link>;
  };

  const displayLabeledLink = (fieldName, label) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
      if (val == null || val === "") return "—";
    }
    return <Link href={val} external>{label}</Link>;
  };

  const displayAlias = (fieldName) => {
    let val = getValue(fieldName);
    if (val == null || val === "") return "—";
    if (Array.isArray(val)) {
      return val.filter(v => v).map((alias, i) => (
        <span key={i}>{i > 0 && ", "}<Link href={`https://phonetool.amazon.com/users/${alias}`} target="_blank">{alias}</Link></span>
      ));
    }
    return <Link href={`https://phonetool.amazon.com/users/${val}`} target="_blank">{val}</Link>;
  };

  // --- Conditional field helpers ---

  const isSoxInScope = () => {
    const val = getValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT);
    if (Array.isArray(val)) {
      return val.includes("In Scope");
    }
    return val === "In Scope";
  };

  const generateEmbedableBroadcastLinkFromVideoLink = (url) => {
    let videoId = "";
    let embedableLink = "https://broadcast.amazon.com/embed/";
    let urlSplit = url?.replace(/\/$/, '').split("/");
    let foundVideosIndex = urlSplit?.findIndex((e) => e === 'videos');
    if (foundVideosIndex !== -1 && urlSplit) {
      videoId = urlSplit[foundVideosIndex + 1];
    }
    if (videoId && videoId !== "") {
      return `${embedableLink}${videoId}`;
    }
    return embedableLink;
  };

  const getDemoVideoUrl = () => {
    let val = getValue(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL);
    if (Array.isArray(val)) {
      val = val.length > 0 ? val[0] : null;
    }
    return val || "";
  };

  // --- Edit Mode Functions (TemplateOak pattern) ---

  const cancelEdition = () => {
    setRenderJsonEdited(JSON.parse(JSON.stringify(renderJson)));
    setErrorMessage("");
    setIsEditMode(false);
    setUpdateComments("");
  };

  const editApplication = () => {
    setIsEditMode(true);
    setRenderJsonEdited(JSON.parse(JSON.stringify(renderJson)));
    setErrorMessage("");
  };

  // --- Mandatory Field Validation (Task 6.1) ---

  const AI_APP_MANDATORY_FIELDS = [
    TEMPLATE_APP.APP_NAME,
    TEMPLATE_APP.APP_DESCRIPTION,
    AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE,
    AI_APP_SPECIFIC_FIELDS.TAGS,
    AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE,
    TEMPLATE_APP.ORGANIZATION,
    AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL,
    AI_APP_SPECIFIC_FIELDS.APP_OWNER,
    TEMPLATE_APP.PROCESS_OWNER,
    TEMPLATE_APP.GRC_CONTROL_OWNER,
    TEMPLATE_APP.VP,
    TEMPLATE_APP.REGION,
    TEMPLATE_APP.ANNUAL_MER_SAVINGS,
    AI_APP_SPECIFIC_FIELDS.SOX_IMPACT,
    AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK,
    AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT,
    AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME,
    AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL
  ];

  const FIELD_LABELS = {
    [TEMPLATE_APP.APP_NAME]: "Application Name",
    [TEMPLATE_APP.APP_DESCRIPTION]: "Description",
    [AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE]: "Use Case Type",
    [AI_APP_SPECIFIC_FIELDS.TAGS]: "Tags",
    [AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE]: "Solution Type",
    [TEMPLATE_APP.ORGANIZATION]: "Organization",
    [AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL]: "Automation Tool",
    [AI_APP_SPECIFIC_FIELDS.APP_OWNER]: "Accounting POC",
    [TEMPLATE_APP.PROCESS_OWNER]: "Process Owner (L7)",
    [TEMPLATE_APP.GRC_CONTROL_OWNER]: "GRC Control Owner",
    [TEMPLATE_APP.VP]: "VP",
    [TEMPLATE_APP.REGION]: "Region",
    [TEMPLATE_APP.ANNUAL_MER_SAVINGS]: "Estimated Annual MER (hours)",
    [AI_APP_SPECIFIC_FIELDS.SOX_IMPACT]: "SOX Impact",
    [AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK]: "Application Link",
    [AI_APP_SPECIFIC_FIELDS.GRC_LINK]: "Link to GRC",
    [AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT]: "Overview Content",
    [AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME]: "Support Contact Name",
    [AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL]: "Support Email",
  };

  const getMissingFields = (data, fields) => {
    let missing = [];
    // Add GRC_LINK as mandatory if SOX Impact is "In Scope"
    let allFields = [...fields];
    const soxVal = data?.[AI_APP_SPECIFIC_FIELDS.SOX_IMPACT];
    const soxIsInScope = Array.isArray(soxVal) ? soxVal.includes("In Scope") : soxVal === "In Scope";
    if (soxIsInScope && !allFields.includes(AI_APP_SPECIFIC_FIELDS.GRC_LINK)) {
      allFields.push(AI_APP_SPECIFIC_FIELDS.GRC_LINK);
    }
    allFields.forEach((field) => {
      const val = data?.[field];
      if (val == null || val === "" || (Array.isArray(val) && val.length === 0)) {
        missing.push(FIELD_LABELS[field] || field);
      }
    });
    return missing;
  };

  // --- CRUD Operations (Tasks 5.1, 5.2, 5.3) ---

  // Convert array values to strings for single-select fields before sending to API
  const normalizeAppDataForMutation = (appData) => {
    const singleValueFields = [
      AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE,
      AI_APP_SPECIFIC_FIELDS.SOX_IMPACT,
      AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK,
      AI_APP_SPECIFIC_FIELDS.GRC_LINK,
      AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK,
      AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT,
      AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL,
      AI_APP_SPECIFIC_FIELDS.SOP_URL,
      AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME,
      AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL,
    ];
    singleValueFields.forEach((field) => {
      if (Array.isArray(appData[field])) {
        appData[field] = appData[field].length > 0 ? appData[field][0] : null;
      }
    });
    return appData;
  };

  const addApplication = async () => {
    // 1. Validate mandatory fields
    let missing = getMissingFields(renderJsonEdited, AI_APP_MANDATORY_FIELDS);
    if (missing.length > 0) {
      setErrorMessage("missing mandatory fields : " + missing.join(", "));
      return;
    }

    // Validate Demo Video URL format
    const demoUrl = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL];
    const demoUrlVal = Array.isArray(demoUrl) ? demoUrl[0] : demoUrl;
    if (demoUrlVal && !demoUrlVal.startsWith("https://broadcast.amazon.com/videos/")) {
      setErrorMessage("Demo Video URL must start with https://broadcast.amazon.com/videos/");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    // 2. Set template_id, created_by, last_updated_by, status
    let appData = JSON.parse(JSON.stringify(renderJsonEdited));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData[TEMPLATE_APP.STATUS] = "Active";
    appData.created_by = userId;
    appData.last_updated_by = userId;
    normalizeAppDataForMutation(appData);

    // 3. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP", "ADD_REQ#" + timestamp, "", JSON.stringify(appData), JSON.stringify(appData), "ADD REQ", userId);

    // 4. Call addAIApp
    addAIApp(appData,
      (response) => {
        setShowLoadingIcon(false);
        const addedApp = response?.data?.addAIApp;
        if (props?.onSave) {
          props.onSave(addedApp);
        } else {
          history.push("/AppsLibrary");
        }
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to add application");
      }
    );
  };

  const updateApplication = async () => {
    // 1. Validate mandatory fields
    let missing = getMissingFields(renderJsonEdited, AI_APP_MANDATORY_FIELDS);
    if (missing.length > 0) {
      setErrorMessage("missing mandatory fields : " + missing.join(", "));
      return;
    }

    // Validate Demo Video URL format
    const demoUrl = renderJsonEdited?.[AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL];
    const demoUrlVal = Array.isArray(demoUrl) ? demoUrl[0] : demoUrl;
    if (demoUrlVal && !demoUrlVal.startsWith("https://broadcast.amazon.com/videos/")) {
      setErrorMessage("Demo Video URL must start with https://broadcast.amazon.com/videos/");
      return;
    }

    // 2. Require comments
    if (!updateComments || updateComments.trim() === "") {
      setErrorMessage("Please provide your comments for the update");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    // 3. Set template_id, last_updated_by
    let appData = JSON.parse(JSON.stringify(renderJsonEdited));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData.last_updated_by = userId;
    normalizeAppDataForMutation(appData);

    // 4. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP#" + id, timestamp, updateComments, JSON.stringify(renderJson), JSON.stringify(appData), "UPDATE REQ", userId);

    // 5. Call updateAIApp
    updateAIApp(appData,
      (response) => {
        setShowLoadingIcon(false);
        const updatedApp = response?.data?.updateAIApp;
        if (updatedApp) {
          setRenderJson(updatedApp);
          setRenderJsonEdited(JSON.parse(JSON.stringify(updatedApp)));
        }
        setErrorMessage("");
        setIsEditMode(false);
        setUpdateComments("");
        if (id) loadAuditLogs(id);
        if (props?.onSave) {
          props.onSave(updatedApp);
        }
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to update application");
      }
    );
  };

  const deleteApplication = async () => {
    // 1. Require comments
    if (!updateComments || updateComments.trim() === "") {
      setModalError("Please provide comments with the reason for deletion");
      return;
    }

    setShowLoadingIcon(true);
    setModalError("");

    // 2. Check existing approvals
    try {
      let existingApprovals = await getApprovalsForApp(id);
      if (existingApprovals && existingApprovals.length > 0) {
        setShowLoadingIcon(false);
        setModalMessage("This app is already scheduled for deletion. Approval id : " + existingApprovals[0].workflowInstanceId);
        setDeleteConfirmationModal(false);
        return;
      }
    } catch (err) {
      setShowLoadingIcon(false);
      setModalMessage("Something went wrong. Cannot fetch existing approvals for the app.");
      setDeleteConfirmationModal(false);
      return;
    }

    // 3. Create audit log
    let timestamp = new Date().toISOString();
    createAuditLog("APP#" + id, "DEL_REQ#" + timestamp, updateComments, JSON.stringify(renderJson), "", "DEL REQ", userId);

    // 4. Start WAS approval
    try {
      let approvalResponse = await startWASApproval({
        userAlias: userId,
        stage: currentStage,
        appId: id,
        appName: renderJson?.app_name,
        comments: updateComments,
        approveResponseNeeded: 1
      });

      let workflowInstanceId = approvalResponse?.workflowInstanceId;

      // 5. Delete with approval
      deleteAppWithApproval(id, workflowInstanceId,
        (response) => {
          setShowLoadingIcon(false);
          setDeleteConfirmationModal(false);
          setModalMessage("Deletion request submitted. Approval ID: " + workflowInstanceId);
        },
        (error) => {
          setShowLoadingIcon(false);
          setDeleteConfirmationModal(false);
          setModalMessage("Could not auto-trigger deletion. Approval ID: " + workflowInstanceId + ". Please follow up manually.");
        }
      );
    } catch (err) {
      setShowLoadingIcon(false);
      setDeleteConfirmationModal(false);
      setModalMessage("Could not create approval for deletion. Please try again.");
    }
  };

  // --- Save Unpublished App (uses updateAutomatedApp API) ---

  const saveUnpublishedApp = async () => {
    setShowLoadingIcon(true);
    setErrorMessage("");

    let appData = JSON.parse(JSON.stringify(renderJsonEdited || {}));
    appData[TEMPLATE_APP.TEMPLATE_ID] = TEMPLATES.AI;
    appData.last_updated_by = userId;

    updateAutomatedApp(appData,
      (response) => {
        setShowLoadingIcon(false);
        if (props?.onSave) {
          props.onSave(response);
        }
      },
      (error) => {
        setShowLoadingIcon(false);
        setErrorMessage(error?.errors?.[0]?.message || "Failed to save application");
      }
    );
  };

  // --- Favorites/Featured (Task 7.1) ---

  const handleRatingChange = (rating) => {
    const oldRating = userRating;
    const oldAvg = displayAvgRating;
    const oldCount = displayRatingCount;

    // Optimistic update
    setUserRating(rating);
    if (rating === 0 && oldRating > 0) {
      // Removing rating
      const newCount = Math.max(oldCount - 1, 0);
      const newAvg = newCount > 0 ? ((oldAvg * oldCount) - oldRating) / newCount : 0;
      setDisplayRatingCount(newCount);
      setDisplayAvgRating(newAvg);
      deleteRating(id, "APP", () => {}, () => {
        // Revert on failure
        setUserRating(oldRating);
        setDisplayAvgRating(oldAvg);
        setDisplayRatingCount(oldCount);
      });
    } else {
      // Adding or updating rating
      const newCount = oldRating === 0 ? oldCount + 1 : oldCount;
      const newAvg = oldRating === 0
        ? ((oldAvg * oldCount) + rating) / newCount
        : ((oldAvg * oldCount) - oldRating + rating) / newCount;
      setDisplayRatingCount(newCount);
      setDisplayAvgRating(newAvg);
      submitRating(id, "APP", rating, () => {}, () => {
        // Revert on failure
        setUserRating(oldRating);
        setDisplayAvgRating(oldAvg);
        setDisplayRatingCount(oldCount);
      });
    }
  };

  const markFavorite = async (appId) => {
    setFavDisabled(true);
    await favorite(appId, userId, function onAdded(result) {
      setIsFavorite(true);
    }, function onDeleted(result) {
      setIsFavorite(false);
    }, function onFailure(result) {
    });
    setFavDisabled(false);
  };

  const markFeatured = async (appId) => {
    await featured(appId, function onAdded(result) {
      setFeaturedPressed(true);
    }, function onDeleted(result) {
      setFeaturedPressed(false);
    }, function onFailure(result) {
    });
  };

  // --- Lifecycle ---

  useEffect(() => {
    setInitView();
    return () => {};
  }, [id]);

  useEffect(() => {
    if (props?.renderJson) {
      setRenderJson(props.renderJson);
      setRenderJsonEdited(props.renderJson);
      setDisplayAvgRating(parseFloat(props.renderJson?.[TEMPLATE_APP.RATING_AVG]) || 0);
      setDisplayRatingCount(parseInt(props.renderJson?.[TEMPLATE_APP.RATING_COUNT]) || 0);
    }
  }, [props?.renderJson]);

  // --- Initialization ---

  const setInitView = async () => {
    if (id) {
      setIsEditMode(props?.isEditOverride || false);
      setIsNewApp(false);
      // Check favorite/featured status for existing apps
      checkIsFavorite(id, userId, (result) => setIsFavorite(result));
      checkIsFeatured(id, (result) => setFeaturedPressed(result));
    } else {
      setIsNewApp(true);
      setIsEditMode(true);
    }
    loadEditableFields();
    if (id) {
      loadAuditLogs(id);
      // Fetch user's rating for this app
      getUserRatings("APP",
        (result) => {
          const ratings = result?.data?.getUserRatings || [];
          const myRating = ratings.find(r => r.entity_id === id);
          if (myRating) setUserRating(myRating.rating);
        },
        () => {}
      );
    }

    let accessPerms = await canAddEditDeleteApplication(userId, TEMPLATES.AI, currentStage);
    setCanDelete(accessPerms?.delete);
    setCanEdit(accessPerms?.edit);
    setCanAdd(accessPerms?.add);

    if (!id && accessPerms?.add) {
      setIsNewApp(true);
      if (!isEditMode) setIsEditMode(true);
    }

    if (!id && !accessPerms?.add) {
      setAlertErrorMessage({
        header: "Access denied",
        message: "No access to add an application",
      });
    } else {
      setAlertErrorMessage(null);
    }

    setShowLoadingIcon(false);
  };

  const loadEditableFields = async () => {
    let res = await getEditableFields();
    setFieldMetadata(res);
  };

  const loadAuditLogs = async (appId) => {
    setAuditLogsLoading(true);
    try {
      const logs = await getLogsById(`APP#${appId}`);
      // Show BOT entries (confirmed updates) with user comments from matching REQ
      // Show orphan REQ entries (no matching BOT) as pending/failed
      const allLogs = logs || [];
      const botLogs = allLogs.filter(l => l.actor === "BOT");
      const reqLogs = allLogs.filter(l => l.actor !== "BOT");

      const combined = [];
      const matchedReqs = new Set();

      botLogs.forEach(botLog => {
        // Find the closest REQ entry that came just before this BOT entry
        const matchingReq = reqLogs.find((req, idx) => {
          if (matchedReqs.has(idx)) return false;
          const botTs = parseFloat(botLog.last_updated_time_stamp || botLog.sk || 0);
          const reqTs = parseFloat(req.last_updated_time_stamp || req.sk || 0);
          // REQ should be within 30 seconds before BOT
          return botTs >= reqTs && (botTs - reqTs) < 30;
        });

        if (matchingReq) {
          matchedReqs.add(reqLogs.indexOf(matchingReq));
          combined.push({
            ...botLog,
            comments: matchingReq.comments || botLog.comments,
            actor: matchingReq.actor,
            action: botLog.action === "AI APP UPDATED" ? "Update Complete" : botLog.action === "AI APP CREATED" ? "Created" : botLog.action,
          });
        } else {
          combined.push({
            ...botLog,
            action: botLog.action === "AI APP UPDATED" ? "Update Complete" : botLog.action === "AI APP CREATED" ? "Created" : botLog.action,
            actor: "System",
          });
        }
      });

      // Add unmatched REQ entries as "Pending"
      reqLogs.forEach((req, idx) => {
        if (!matchedReqs.has(idx)) {
          combined.push({
            ...req,
            action: req.action === "UPDATE REQ" ? "Update Initiated" : req.action === "ADD REQ" ? "Create Initiated" : req.action,
          });
        }
      });

      // Sort by timestamp descending
      combined.sort((a, b) => {
        const tsA = parseFloat(a.last_updated_time_stamp || a.sk || 0);
        const tsB = parseFloat(b.last_updated_time_stamp || b.sk || 0);
        return tsB - tsA;
      });

      setAuditLogs(combined);
    } catch (e) {
      console.error("Failed to load audit logs", e);
      setAuditLogs([]);
    }
    setAuditLogsLoading(false);
  };

  // --- Render (preserving existing UI layout) ---

  if (showLoadingIcon) {
    return (
      <AppLayout
        content={
          <Box textAlign="center" padding={{ top: "xxxl" }}>
            <Spinner size="large" />
            <Box variant="p" margin={{ top: "s" }}>Loading...</Box>
          </Box>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "Application Marketplace", href: "/AppsLibrary" },
              { text: "AI App", href: "/AIApp" },
            ]}
          />
        }
        navigationHide
        toolsHide
      />
    );
  }

  if (alertErrorMessage) {
    return (
      <AppLayout
        content={
          <Box padding={{ top: "l" }}>
            <Alert type="error" header={alertErrorMessage.header}>
              {alertErrorMessage.message}
            </Alert>
          </Box>
        }
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "Application Marketplace", href: "/AppsLibrary" },
              { text: "AI App", href: "/AIApp" },
            ]}
          />
        }
        navigationHide
        toolsHide
      />
    );
  }

  return (
    <AppLayout
      content={
        <>
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {id && !isNewApp && (
                    <span
                      onClick={() => { if (!favDisabled) markFeatured(id); }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "4px",
                        padding: "4px 20px", borderRadius: "20px", height: "32px", boxSizing: "border-box",
                        border: "2px solid #0972d3", color: "#0972d3",
                        fontWeight: 700, fontSize: "14px", cursor: favDisabled ? "not-allowed" : "pointer",
                        backgroundColor: featuredPressed ? "#e8f4fd" : "white"
                      }}
                    >
                      <Icon name={featuredPressed ? "star-filled" : "star"} /> {featuredPressed ? "Featured" : "Feature App"}
                    </span>
                  )}
                  {id && !isNewApp && (
                    <Button variant="primary" onClick={() => setAddToCPModalVisible(true)}>Add to Close Portal</Button>
                  )}
                  {!isEditMode && !isNewApp && canEdit && (
                    <Button onClick={editApplication}>Edit</Button>
                  )}
                  {!isNewApp && canDelete && (
                    <Button onClick={() => setDeleteConfirmationModal(true)}>Delete</Button>
                  )}
                </SpaceBetween>
              }
            >
              {isNewApp ? "New AI App" : (renderJson?.app_name || "AI App")}
            </Header>
          }
        >
          <SpaceBetween size="l">
            {alertErrorMessage && (
              <Alert type="error" header={alertErrorMessage.header}>
                {alertErrorMessage.message}
              </Alert>
            )}

            {modalMessage && (
              <Modal
                visible={!!modalMessage}
                onDismiss={() => setModalMessage("")}
                header="Info"
              >
                {modalMessage}
              </Modal>
            )}

            {/* VIEW MODE - Card-style metadata grid */}
            {!isEditMode && !isNewApp && (
              <SpaceBetween size="l">
                {/* AI-Powered badge */}
                <span className="ai-powered-badge"><Icon name="gen-ai" /> AI-Powered</span>

                {/* Description */}
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_DESCRIPTION)}</Box>

                {/* Rating and Views */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <StarRating
                    value={userRating}
                    averageRating={displayAvgRating}
                    ratingCount={displayRatingCount}
                    onChange={handleRatingChange}
                  />
                  <span style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "4px", color: "#5f6b7a" }}>
                    <span>👁️</span>
                    <span>{getValue(TEMPLATE_APP.VIEW_COUNT) != null ? getValue(TEMPLATE_APP.VIEW_COUNT) : "0"} views</span>
                  </span>
                </div>

                {/* Row 1: Status, App Owner, Process Owner, GRC Control Owner */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Status</Box>
                    <Box variant="p">
                      {getValue(TEMPLATE_APP.STATUS) === 'Active' ?
                        <span style={{color:"#00802f"}}><Icon name="status-positive" variant="success" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : getValue(TEMPLATE_APP.STATUS) === 'Deprecated' ?
                        <span style={{color:"#db0000"}}><Icon name="status-negative" variant="error" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : getValue(TEMPLATE_APP.STATUS) === 'WIP' ?
                        <span style={{color:"#0f141a"}}><Icon name="status-pending" variant="normal" />&nbsp;{getValue(TEMPLATE_APP.STATUS)}</span>
                        : <span style={{color:"#855900"}}><Icon name="status-warning" variant="warning" />&nbsp;No status</span>
                      }
                    </Box>
                  </div>
                  <div title="The Accounting point of contact who designed and developed this application.">
                    <Box variant="awsui-key-label">Accounting POC</Box>
                    <Box variant="p">{displayAlias(AI_APP_SPECIFIC_FIELDS.APP_OWNER)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Process Owner</Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.PROCESS_OWNER)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">GRC Control Owner</Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.GRC_CONTROL_OWNER)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 2: VP */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">VP</Box>
                    <Box variant="p">{displayAlias(TEMPLATE_APP.VP)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 3: Created By, Region, Estimated Annual MER, SOX Impact */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Created By</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.CREATED_BY)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Region</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.REGION)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Estimated Annual MER</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.ANNUAL_MER_SAVINGS)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">SOX Impact</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT)}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 4: Organization, Use Case Type, Solution Type, Created */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Organization</Box>
                    <Box variant="p">{displayValue(TEMPLATE_APP.ORGANIZATION)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Use Case Type</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Solution Type</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Created</Box>
                    <Box variant="p">{getValue(TEMPLATE_APP.CREATE_TIME_STAMP) ? new Date(getValue(TEMPLATE_APP.CREATE_TIME_STAMP) * 1000).toLocaleDateString() : "—"}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 5: Last Modified */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Last Modified</Box>
                    <Box variant="p">{getValue(TEMPLATE_APP.LAST_UPDATED_TIME_STAMP) ? new Date(getValue(TEMPLATE_APP.LAST_UPDATED_TIME_STAMP) * 1000).toLocaleDateString() : "—"}</Box>
                  </div>
                </ColumnLayout>

                {/* Row 6: Automation Tool, Permissions, Application Link, Link to GRC */}
                <ColumnLayout columns={4} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Automation Tool</Box>
                    <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL)}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Permissions</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK, "View Permissions")}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Application Link</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK, "Open Application")}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Link to GRC</Box>
                    <Box variant="p">{displayLabeledLink(AI_APP_SPECIFIC_FIELDS.GRC_LINK, "View GRC Controls")}</Box>
                  </div>
                </ColumnLayout>

                {/* Tags */}
                <div>
                  <Box variant="awsui-key-label">Tags</Box>
                  <Box padding={{ top: "xxs" }}>
                    <SpaceBetween direction="horizontal" size="xs">
                      {getValue(AI_APP_SPECIFIC_FIELDS.TAGS) && Array.isArray(getValue(AI_APP_SPECIFIC_FIELDS.TAGS)) ?
                        getValue(AI_APP_SPECIFIC_FIELDS.TAGS).map((tag, index) => (
                          <span key={index} className={["card-label-blue", "card-label-grey", "card-label-red"][index % 3]}>{tag}</span>
                        ))
                        : <Box variant="p">—</Box>
                      }
                    </SpaceBetween>
                  </Box>
                </div>
              </SpaceBetween>
            )}

            {/* EDIT MODE - Form fields */}
            {(isEditMode || isNewApp) && (
              <SpaceBetween size="l">

            {/* Application Name */}
            <FormField label={requiredLabel("Application Name")}>
              {isEditable(TEMPLATE_APP.APP_NAME) ? (
                <CustomInput
                  field={TEMPLATE_APP.APP_NAME}
                  placeholder="Enter application name"
                  getValue={getValue}
                  updateValue={updateValue}
                />
              ) : (
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_NAME)}</Box>
              )}
            </FormField>

            {/* Description */}
            <FormField label={requiredLabel("Description")}>
              {isEditable(TEMPLATE_APP.APP_DESCRIPTION) ? (
                <Textarea
                  value={getValue(TEMPLATE_APP.APP_DESCRIPTION) || ""}
                  placeholder="Detailed description of the application"
                  rows={4}
                  onChange={({ detail }) => updateValue(TEMPLATE_APP.APP_DESCRIPTION, detail.value)}
                />
              ) : (
                <Box variant="p">{displayValue(TEMPLATE_APP.APP_DESCRIPTION)}</Box>
              )}
            </FormField>

            {/* Use Case Type */}
            <FormField label={requiredLabel("Use Case Type")}>
              {isEditable(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE) ? (
                <CustomSelect
                  field={AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE}
                  getValue={getValue}
                  updateValue={updateValue}
                  getOptions={getSelectFieldValues}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.USE_CASE_TYPE)}</Box>
              )}
            </FormField>

            {/* Tags */}
            <FormField label={requiredLabel("Tags")}>
              {isEditable(AI_APP_SPECIFIC_FIELDS.TAGS) ? (
                <CustomMultiSelect
                  field={AI_APP_SPECIFIC_FIELDS.TAGS}
                  updateValue={updateValue}
                  getValues={getSelectFieldValues}
                  getSelectedOptions={getMultiSelectedOptions}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.TAGS)}</Box>
              )}
            </FormField>

            {/* Solution Type */}
            <FormField
              label={requiredLabel("Solution Type")}
              description={isEditable(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE) ? "AI capability classification based on the SOX ML/AI Risk Management Policy. Support: ML/AI provides visuals, exploration, alerts, and other support for human decision makers; the decision is made by humans. Augment: ML/AI generates recommendations and may provide diagnostic analytics for human validation; AI suggests and human decides, or human suggests and machine decides, or both decide together. Automate: Autonomous decision-making using predictions, forecasts, simulations, rules, or optimization; risks managed by guardrails with a human-in-the-loop for exceptional cases." : ""}
            >
              {isEditable(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE) ? (
                <CustomSelect
                  field={AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE}
                  getValue={getValue}
                  updateValue={updateValue}
                  getOptions={getSelectFieldValues}
                />
              ) : (
                <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOLUTION_TYPE)}</Box>
              )}
            </FormField>

            <Header variant="h3">Metadata</Header>

            {/* Organization & Automation Tool */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("Organization")}>
                {isEditable(TEMPLATE_APP.ORGANIZATION) ? (
                  <CustomMultiSelect
                    field={TEMPLATE_APP.ORGANIZATION}
                    updateValue={updateValue}
                    getValues={getSelectFieldValues}
                    getSelectedOptions={getMultiSelectedOptions}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.ORGANIZATION)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Automation Tool")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL) ? (
                  <CustomSelect
                    field={AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL}
                    getValue={getValue}
                    updateValue={updateValue}
                    getOptions={getSelectFieldValues}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.AUTOMATION_TOOL)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Accounting POC & Process Owner (L7) */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("Accounting POC")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.APP_OWNER) ? (
                  <CustomSelectAlias
                    field={AI_APP_SPECIFIC_FIELDS.APP_OWNER}
                    getValue={getValue}
                    updateValue={updateValue}
                    options={suggestedLogins}
                    getOptions={getSuggestedLoginsCaller}
                    aliasesLoading={aliasesLoading}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.APP_OWNER)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Process Owner (L7)")}>
                {isEditable(TEMPLATE_APP.PROCESS_OWNER) ? (
                  <CustomSelectAlias
                    field={TEMPLATE_APP.PROCESS_OWNER}
                    getValue={getValue}
                    updateValue={updateValue}
                    options={suggestedLogins}
                    getOptions={getSuggestedLoginsCaller}
                    aliasesLoading={aliasesLoading}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.PROCESS_OWNER)}</Box>
                )}
              </FormField>
            </Grid>

            {/* GRC Control Owner & VP */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("GRC Control Owner")}>
                {isEditable(TEMPLATE_APP.GRC_CONTROL_OWNER) ? (
                  <CustomSelectAlias
                    field={TEMPLATE_APP.GRC_CONTROL_OWNER}
                    getValue={getValue}
                    updateValue={updateValue}
                    options={suggestedLogins}
                    getOptions={getSuggestedLoginsCaller}
                    aliasesLoading={aliasesLoading}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.GRC_CONTROL_OWNER)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("VP")}>
                {isEditable(TEMPLATE_APP.VP) ? (
                  <Select
                    selectedOption={getValue(TEMPLATE_APP.VP) ? { label: getValue(TEMPLATE_APP.VP), value: getValue(TEMPLATE_APP.VP) } : null}
                    onChange={({ detail }) => updateValue(TEMPLATE_APP.VP, detail.selectedOption.value)}
                    placeholder="Select a VP Alias"
                    options={directReports}
                    loadingText="Loading Aliases..."
                    onLoadItems={() => getDirectReportsCaller('shelleyr')}
                    statusType={directReports?.length <= 0 ? "loading" : "finished"}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.VP)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Region & Estimated Annual MER (hours) */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={requiredLabel("Region")}>
                {isEditable(TEMPLATE_APP.REGION) ? (
                  <CustomMultiSelect
                    field={TEMPLATE_APP.REGION}
                    updateValue={updateValue}
                    getValues={getSelectFieldValues}
                    getSelectedOptions={getMultiSelectedOptions}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.REGION)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Estimated Annual MER (hours)")}>
                {isEditable(TEMPLATE_APP.ANNUAL_MER_SAVINGS) ? (
                  <CustomInput
                    field={TEMPLATE_APP.ANNUAL_MER_SAVINGS}
                    placeholder="e.g., 2,500 hours"
                    getValue={getValue}
                    updateValue={updateValue}
                  />
                ) : (
                  <Box variant="p">{displayValue(TEMPLATE_APP.ANNUAL_MER_SAVINGS)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Permissions Link & SOX Impact */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label="Permissions Link">
                {isEditable(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK) ? (
                  <CustomInput
                    field={AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK}
                    placeholder="URL for requesting access"
                    getValue={getValue}
                    updateValue={updateValue}
                  />
                ) : (
                  <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.PERMISSIONS_LINK)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("SOX Impact")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT) ? (
                  <CustomSelect
                    field={AI_APP_SPECIFIC_FIELDS.SOX_IMPACT}
                    getValue={getValue}
                    updateValue={updateValue}
                    getOptions={getSelectFieldValues}
                  />
                ) : (
                  <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SOX_IMPACT)}</Box>
                )}
              </FormField>
            </Grid>

            {/* Link to GRC & Application Link */}
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
              <FormField label={isSoxInScope() ? requiredLabel("Link to GRC") : "Link to GRC"} description="Only applicable for SOX in Scope applications.">
                {isEditable(AI_APP_SPECIFIC_FIELDS.GRC_LINK) ? (
                  <CustomInput
                    field={AI_APP_SPECIFIC_FIELDS.GRC_LINK}
                    placeholder="https://grc.example.com/controls/..."
                    getValue={getValue}
                    updateValue={updateValue}
                    disabled={!isSoxInScope()}
                  />
                ) : (
                  <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.GRC_LINK)}</Box>
                )}
              </FormField>
              <FormField label={requiredLabel("Application Link")}>
                {isEditable(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK) ? (
                  <CustomInput
                    field={AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK}
                    placeholder="https://apps.example.com/..."
                    getValue={getValue}
                    updateValue={updateValue}
                  />
                ) : (
                  <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.APPLICATION_LINK)}</Box>
                )}
              </FormField>
            </Grid>
            </SpaceBetween>
            )}

            {/* Tabbed sections: Overview, Documentation, Change Log, Support */}
            <Tabs
              tabs={[
                {
                  label: "Overview",
                  id: "overview",
                  content: (
                    <Container header={<Header variant="h3">Overview</Header>}>
                      <SpaceBetween size="l">
                        <FormField 
                          label={isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? requiredLabel("Overview Content") : "About this application"}
                          description={isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? "Supports Markdown: **bold**, *italic*, ## Heading, - bullet points" : ""}
                        >
                          {isEditable(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) ? (
                            <Textarea
                              value={getValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT) || ""}
                              placeholder="Enter detailed overview of the application, key features, benefits, etc."
                              rows={8}
                              onChange={({ detail }) => updateValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT, detail.value)}
                            />
                          ) : (
                            <div className="markdown-content"><ReactMarkdown>{displayValue(AI_APP_SPECIFIC_FIELDS.OVERVIEW_CONTENT)}</ReactMarkdown></div>
                          )}
                        </FormField>
                        <FormField 
                          label={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? "Demo Video URL" : "Demo Video"}
                          description={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? "Must be a Broadcast video URL (https://broadcast.amazon.com/videos/...)" : ""}
                          errorText={isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) && getDemoVideoUrl() && getDemoVideoUrl().trim() !== "" && !getDemoVideoUrl().startsWith("https://broadcast.amazon.com/videos/") ? "URL must start with https://broadcast.amazon.com/videos/" : ""}
                        >
                          {isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL}
                              placeholder="https://broadcast.amazon.com/videos/..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            !getDemoVideoUrl() && <Box variant="p">—</Box>
                          )}
                        </FormField>
                        {!isEditable(AI_APP_SPECIFIC_FIELDS.DEMO_VIDEO_URL) && getDemoVideoUrl() && (
                          <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                            <iframe
                              src={generateEmbedableBroadcastLinkFromVideoLink(getDemoVideoUrl())}
                              style={{ display: "block", width: "100%", height: "56rem", border: "none", borderRadius: "8px" }}
                              allowFullScreen
                              title="Demo Video"
                            />
                          </div>
                        )}
                      </SpaceBetween>
                    </Container>
                  )
                },
                {
                  label: "Documentation",
                  id: "documentation",
                  content: (
                    <Container header={<Header variant="h3">Documentation</Header>}>
                      <SpaceBetween size="l">
                        <Header variant="h4">Data Sources</Header>
                        <ExpandableSection
                          variant="navigation"
                          defaultExpanded={true}
                          headerText="Datasets and Source Systems"
                        >
                          {isEditable(TEMPLATE_APP.SOURCE_DATA_TABLES) ?
                            <CustomMultiHybridInputsForSourceTables
                              field={TEMPLATE_APP.SOURCE_DATA_TABLES}
                              updateValue={updateValue}
                              getValues={getValue}
                            />
                            :
                            <Box variant="p">
                              {getValue(TEMPLATE_APP.SOURCE_DATA_TABLES) ? <Table
                                columnDefinitions={[
                                  {
                                    id: "upstream_source",
                                    header: "Upstream Source",
                                    cell: item => item?.upstream_source,
                                    isRowHeader: true
                                  },
                                  {
                                    id: "source_database",
                                    header: "Database",
                                    cell: item => item?.source_database,
                                  },
                                  {
                                    id: "source_schema",
                                    header: "Schema",
                                    cell: item => item?.source_schema,
                                  },
                                  {
                                    id: "source_table",
                                    header: "Table",
                                    cell: item => item?.source_table,
                                  },
                                  {
                                    id: "description",
                                    header: "Description",
                                    cell: item => item?.description,
                                  }
                                ]}
                                items={getValue(TEMPLATE_APP.SOURCE_DATA_TABLES) || []}
                                loadingText="Loading datasets"
                                sortingDisabled
                                empty={
                                  <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                    <SpaceBetween size="m">
                                      <b>No datasets</b>
                                    </SpaceBetween>
                                  </Box>
                                }
                              /> : <Box>No datasets added</Box>}
                            </Box>
                          }
                        </ExpandableSection>

                        <Header variant="h4">Available Resources</Header>
                        <FormField label="Process Design Document URL">
                          {isEditable(TEMPLATE_APP.PDD) ? (
                            <CustomInput
                              field={TEMPLATE_APP.PDD}
                              placeholder="https://..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayLink(TEMPLATE_APP.PDD)}</Box>
                          )}
                        </FormField>
                        <FormField label="Steps of Preparation (SOP) URL">
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SOP_URL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SOP_URL}
                              placeholder="https://..."
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayLink(AI_APP_SPECIFIC_FIELDS.SOP_URL)}</Box>
                          )}
                        </FormField>
                      </SpaceBetween>
                    </Container>
                  )
                },
                {
                  label: "Change Log",
                  id: "changelog",
                  content: (
                    <Container header={<Header variant="h3">Change Log</Header>}>
                      {auditLogsLoading ? (
                        <SkeletonCard count={3} />
                      ) : auditLogs.length > 0 ? (
                        <SpaceBetween size="l">
                        <Cards
                          cardDefinition={{
                            header: item => {
                              const isComplete = item.action?.includes("Complete") || item.action === "Created";
                              const isInitiated = item.action?.includes("Initiated");
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                                    {isComplete && <Icon name="status-positive" variant="success" />}
                                    {isInitiated && <Icon name="status-pending" variant="warning" />}
                                  </span>
                                  <span style={{ color: isComplete ? "#00802f" : isInitiated ? "#d4760a" : "#0f141a", fontWeight: 700, fontSize: "14px" }}>
                                    {item.action}
                                  </span>
                                  <span style={{ color: "#5f6b7a", fontWeight: 400, fontSize: "14px" }}>by {item.actor || "—"}</span>
                                </div>
                              );
                            },
                            sections: [
                              {
                                id: "comments",
                                content: item => item.comments || "—"
                              },
                              {
                                id: "timestamp",
                                content: item => {
                                  const ts = item.last_updated_time_stamp || item.sk;
                                  if (!ts) return "—";
                                  const date = isNaN(ts) ? new Date(ts) : new Date(parseFloat(ts));
                                  if (isNaN(date.getTime())) return ts;
                                  return date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
                                }
                              }
                            ]
                          }}
                          cardsPerRow={[{ minWidth: 0, cards: 1 }]}
                          items={auditLogs.slice((changeLogPage - 1) * CHANGE_LOG_PAGE_SIZE, changeLogPage * CHANGE_LOG_PAGE_SIZE)}
                          empty={
                            <Box textAlign="center" color="inherit" padding={{ top: "l" }}>
                              <b>No change logs</b>
                            </Box>
                          }
                        />
                        <Box float="right">
                          <Pagination
                            currentPageIndex={changeLogPage}
                            pagesCount={Math.ceil(auditLogs.length / CHANGE_LOG_PAGE_SIZE)}
                            onChange={({ detail }) => setChangeLogPage(detail.currentPageIndex)}
                          />
                        </Box>
                        </SpaceBetween>
                      ) : (
                        <Box variant="p" color="text-body-secondary">
                          Change log will be automatically generated based on application updates after submission.
                        </Box>
                      )}
                    </Container>
                  )
                },
                {
                  label: "Support",
                  id: "support",
                  content: (
                    <Container header={<Header variant="h3">Support</Header>}>
                      <SpaceBetween size="l">
                        <FormField label={requiredLabel("Support Contact Name")}>
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME}
                              placeholder="Enter support contact name"
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SUPPORT_CONTACT_NAME)}</Box>
                          )}
                        </FormField>
                        <FormField label={requiredLabel("Support Email")}>
                          {isEditable(AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL) ? (
                            <CustomInput
                              field={AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL}
                              placeholder="support@example.com"
                              getValue={getValue}
                              updateValue={updateValue}
                            />
                          ) : (
                            <Box variant="p">{displayValue(AI_APP_SPECIFIC_FIELDS.SUPPORT_EMAIL)}</Box>
                          )}
                        </FormField>
                        {!isNewApp && !props?.isEditOverride ? (
                          <>
                            <Header variant="h4">Need Help Scaling?</Header>
                            <Box variant="p">
                              If you need help scaling this solution for your particular use case, you can reach out to ATEAM for assistance.
                            </Box>
                            <Link href="https://t.corp.amazon.com/create/templates/9c41d7b9-b693-433e-950a-ec29fcfce9f5" external>Submit a Support Ticket</Link>
                          </>
                        ) : (
                          <>
                            <Header variant="h4">Additional Information</Header>
                            <Box variant="p" color="text-body-secondary">
                              ATEAM support integration will be available after the application is submitted and reviewed.
                            </Box>
                          </>
                        )}
                      </SpaceBetween>
                    </Container>
                  )
                }
              ]}
            />

            {/* Error message at the bottom */}
            {errorMessage && <Alert type="error">{errorMessage}</Alert>}

            {/* Action buttons at the bottom */}
            {isEditMode && (
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => {
                    if (props?.onCancel) {
                      props.onCancel();
                    } else if (isNewApp) {
                      history.push("/AppsLibrary");
                    } else {
                      cancelEdition();
                    }
                  }}>
                    Cancel
                  </Button>
                  {props?.isEditOverride && (
                    <Button onClick={() => saveUnpublishedApp()}>
                      Save
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => isNewApp ? addApplication() : updateApplication()}>
                    {isNewApp ? "Publish" : "Update"}
                  </Button>
                </SpaceBetween>
              </Box>
            )}

            {/* Update Comments - shown in edit mode for existing apps only */}
            {isEditMode && !isNewApp && (
              <FormField label="Update Comments" constraintText="Required for updates">
                <Input
                  value={updateComments}
                  placeholder="Describe what changed and why"
                  onChange={({ detail }) => setUpdateComments(detail.value)}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Container>

        {/* Delete Confirmation Modal (Task 8.2) */}
        {deleteConfirmationModal && (
          <Modal
            visible={deleteConfirmationModal}
            onDismiss={() => {
              setDeleteConfirmationModal(false);
              setDeleteAppName("");
              setUpdateComments("");
              setModalError("");
            }}
            header="Confirm Deletion"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => {
                    setDeleteConfirmationModal(false);
                    setDeleteAppName("");
                    setUpdateComments("");
                    setModalError("");
                  }}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={deleteAppName !== renderJson?.app_name}
                    onClick={deleteApplication}
                  >
                    Confirm Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box>
                To confirm deletion, type the application name: <Box variant="strong">{renderJson?.app_name}</Box>
              </Box>
              <FormField label="Application Name">
                <Input
                  value={deleteAppName}
                  placeholder="Type application name to confirm"
                  onChange={({ detail }) => setDeleteAppName(detail.value)}
                />
              </FormField>
              <FormField label={requiredLabel("Reason for Deletion")}>
                <Input
                  value={updateComments}
                  placeholder="Provide reason for deletion"
                  onChange={({ detail }) => setUpdateComments(detail.value)}
                />
              </FormField>
              {modalError && <Alert type="error">{modalError}</Alert>}
            </SpaceBetween>
          </Modal>
        )}

        {/* Add to Close Portal Modal */}
        {addToCPModalVisible && (
          <AddToCPModal
            visible={addToCPModalVisible}
            onDismiss={() => setAddToCPModalVisible(false)}
            resourceURL={`${location.pathname}${location.search}`}
          />
        )}
        </>
      }
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Application Marketplace", href: "/AppsLibrary" },
            { text: isNewApp ? "New AI App" : (renderJson?.app_name || "AI App"), href: "/AIApp" },
          ]}
        />
      }
      navigationHide
      toolsHide
    />
  );
}

export default AIAppPage;
